import React, { useEffect, useMemo, useState } from 'react';
import { FormField } from '@/components/FormField';
import { Select } from '@/components/Select';
import { Card, SectionHeader } from '@/components/Card';
import { MapPin, Target, Info, AlertTriangle, Activity, FormInput, ExternalLink, RefreshCw } from 'lucide-react';
import { facebookLeadFormsService, LeadGenFormSummary } from '@/services/facebook-lead-forms.service';
import { BaseAdSetForm } from './BaseAdSetForm';
import { AttributionWindowSection, buildAttributionSpec } from './AttributionWindowSection';
import { ODAX_MATRIX, GOAL_LABEL, ConversionLocation } from './odaxMatrix';
import {
  FbAppPicker,
  FbPagePicker,
  FbPixelPicker,
  IgAccountPicker,
  type PlacementValue,
} from '@/components/facebook/MetaResourcePickers';
import type { MetaApp } from '@/services/meta-resources.service';
import { appOsTargeting } from './appTargeting';

// Conversion events supported by Meta for the LEADS objective (per the
// ODAX matrix). When the user picks WEBSITE + a conversion goal, they must
// pick which event to optimise for.
const LEAD_CONV_EVENTS = [
  'LEAD',
  'COMPLETE_REGISTRATION',
  'CONTACT',
  'FIND_LOCATION',
  'SCHEDULE',
  'START_TRIAL',
  'SUBMIT_APPLICATION',
  'SUBSCRIBE',
];

const LEADS_LOCATIONS = ODAX_MATRIX.OUTCOME_LEADS;

const GOAL_HINTS: Record<string, { tone: 'info' | 'warning'; body: string }> = {
  LEAD_GENERATION: {
    tone: 'info',
    body: 'Optimises for instant-form submissions. Most permissive lead-gen goal.',
  },
  QUALITY_LEAD: {
    tone: 'info',
    body: 'Optimises for higher-quality leads (Meta filters out likely-spam submissions). Slightly higher CPL but better lead quality.',
  },
  QUALITY_CALL: {
    tone: 'warning',
    body: 'Calls destination only. Requires the Page to have a verified phone number for click-to-call.',
  },
  OFFSITE_CONVERSIONS: {
    tone: 'warning',
    body: 'Requires a Meta Pixel + a fired conversion event (e.g. CompleteRegistration) on your site.',
  },
  ONSITE_CONVERSIONS: {
    tone: 'info',
    body: 'On-site Meta destinations (Shops, Marketplace). Rare for B2B.',
  },
  LANDING_PAGE_VIEWS: { tone: 'info', body: 'Cheaper than conversions; counts only fully-loaded landing pages.' },
  LINK_CLICKS:        { tone: 'info', body: 'Cheapest signal but lowest qualified intent.' },
  IMPRESSIONS:        { tone: 'info', body: 'Cheap reach with no behavioural signal.' },
  REACH:              { tone: 'info', body: 'Optimises for unique people reached.' },
};

export const LeadsAdSetForm: React.FC = () => {
  const [location, setLocation] = useState<ConversionLocation>('INSTANT_FORM');
  const [optGoal, setOptGoal] = useState<string>('LEAD_GENERATION');
  const [pageId, setPageId] = useState('');
  const [igActorId, setIgActorId] = useState<string | null>(null);
  const [placement, setPlacement] = useState<PlacementValue>({ mode: 'automatic' });
  const [clickWin, setClickWin] = useState('7');
  const [viewWin, setViewWin] = useState('1');
  // Pixel + event — only relevant when the conversion goal needs them.
  const [pixelId, setPixelId] = useState('');
  const [eventType, setEventType] = useState('LEAD');

  const locationEntry = useMemo(
    () => LEADS_LOCATIONS.find((l) => l.value === location)!,
    [location],
  );

  useEffect(() => {
    if (!locationEntry.goals.includes(optGoal)) setOptGoal(locationEntry.goals[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const goalOptions = locationEntry.goals.map((g) => ({ code: g, label: GOAL_LABEL[g] ?? g }));

  const isWebsiteOrApp = location === 'WEBSITE' || location === 'APP';
  const needsPagePicker = !isWebsiteOrApp;
  // Pixel + event only required when the goal is conversion-based.
  const needsPixel = location === 'WEBSITE' && optGoal === 'OFFSITE_CONVERSIONS';
  const [fbAppId, setFbAppId] = useState('');
  const [pickedApp, setPickedApp] = useState<MetaApp | null>(null);
  const objectStoreUrl =
    pickedApp?.objectStoreUrls?.google_play ??
    pickedApp?.objectStoreUrls?.iphone_app_store ??
    pickedApp?.objectStoreUrls?.amazon_app_store ??
    '';
  const isInstantForm = location === 'INSTANT_FORM';
  const isFormGoal = optGoal === 'LEAD_GENERATION' || optGoal === 'QUALITY_LEAD';

  // ── Empty-Page-forms detection ──────────────────────────────────────────
  // LEAD_GENERATION / QUALITY_LEAD require at least one Lead Form on the
  // Page. Pre-fetch so we can warn BEFORE save.
  const [leadForms, setLeadForms] = useState<LeadGenFormSummary[] | null>(null);
  const [formsCheckLoading, setFormsCheckLoading] = useState(false);
  const needsLeadFormCheck = isInstantForm && isFormGoal;

  useEffect(() => {
    if (!needsLeadFormCheck) {
      setLeadForms(null);
      return;
    }
    let cancelled = false;
    setFormsCheckLoading(true);
    facebookLeadFormsService.list()
      .then((r) => { if (!cancelled) setLeadForms(r); })
      .catch(() => { if (!cancelled) setLeadForms([]); })
      .finally(() => { if (!cancelled) setFormsCheckLoading(false); });
    return () => { cancelled = true; };
  }, [needsLeadFormCheck]);

  const showNoLeadFormsBanner = needsLeadFormCheck
    && !formsCheckLoading
    && leadForms !== null
    && leadForms.length === 0;

  // ── Attribution-window auto-clamp ───────────────────────────────────────
  // Meta only accepts (7d click, 1d view) for Lead-objective ad sets when
  // location=WEBSITE + goal=OFFSITE_CONVERSIONS / ONSITE_CONVERSIONS. Every
  // other Lead combo requires (1d click, 0d view). Auto-clamp so users
  // don't hit Meta's "supported combination... (1, 0)" rejection.
  const allowsLongerAttribution = location === 'WEBSITE' && optGoal === 'OFFSITE_CONVERSIONS';

  useEffect(() => {
    if (allowsLongerAttribution) return;
    if (clickWin !== '1') setClickWin('1');
    if (viewWin !== '0') setViewWin('0');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowsLongerAttribution]);

  const igAllowed =
    placement.mode === 'automatic' ||
    !placement.publisher_platforms ||
    placement.publisher_platforms.includes('instagram');

  const extraPayload = () => {
    const out: Record<string, unknown> = {};
    if (locationEntry.destinationType) out.destination_type = locationEntry.destinationType;
    const promoted: Record<string, string> = {};
    if (pageId && needsPagePicker) promoted.page_id = pageId;
    if (needsPixel && pixelId) {
      promoted.pixel_id = pixelId;
      promoted.custom_event_type = eventType;
    }
    // App destination: "Application is required in Promoted Object for App Destination Type" (live, 2026-09-29).
    if (location === 'APP' && fbAppId) {
      promoted.application_id = fbAppId;
      if (objectStoreUrl) promoted.object_store_url = objectStoreUrl;
    }
    if (Object.keys(promoted).length > 0) out.promoted_object = promoted;
    // instagram_actor_id is a creative-level field — Meta + our DTO reject it
    // on the ad-set payload. The IG account flows through the creative.
    out.attribution_spec = buildAttributionSpec(clickWin, viewWin);
    return out;
  };

  const validateExtra = () => {
    if (needsPagePicker && !pageId) return 'Pick a Facebook Page first';
    if (needsPixel && !pixelId) return 'Pick a Meta Pixel — required for Conversions optimisation';
    if (location === 'APP' && !fbAppId) return 'Pick a Facebook App for the App destination';
    return null;
  };

  const extraTargeting = () => (location === 'APP' ? appOsTargeting(objectStoreUrl) : undefined);

  return (
    <BaseAdSetForm
      optimizationGoals={goalOptions}
      extraPayload={extraPayload}
      extraTargeting={extraTargeting}
      validateExtra={validateExtra}
      onPlacementChange={setPlacement}
      onOptimizationGoalChange={setOptGoal}
      expectedObjective="OUTCOME_LEADS"
    >
      {/* Conversion-location picker drives valid goals per Meta's matrix. */}
      <Card padded className="!mt-5">
        <SectionHeader
          title="Where leads come from"
          icon={<Target size={16} />}
          subtitle="Different lead destinations unlock different optimisation goals on Meta."
        />
        <FormField label="Conversion location" required>
          <select
            value={location}
            onChange={(e) => setLocation(e.target.value as ConversionLocation)}
            className="h-10 w-full rounded-lg border border-border bg-surface px-3 text-sm focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary-soft"
          >
            {LEADS_LOCATIONS.map((l) => (
              <option key={l.value} value={l.value}>{l.label}</option>
            ))}
          </select>
        </FormField>
      </Card>

      {/* Empty-Page-forms banner — fires when location=Instant Forms but the
          Page has no Lead Forms yet. Shows BEFORE save so users fix the
          root cause instead of hitting Meta's rejection. */}
      {showNoLeadFormsBanner && (
        <div className="flex items-start gap-3 rounded-2xl border border-warning/40 bg-warning-soft px-4 py-3.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-warning text-white">
            <FormInput size={18} />
          </div>
          <div className="min-w-0 flex-1 space-y-2">
            <div>
              <div className="font-semibold text-text-primary">No Lead Forms on this Page yet</div>
              <p className="mt-0.5 text-xs text-text-secondary leading-relaxed">
                Instant-form lead ads need at least one published Lead Form on the Page. Without one,
                Meta has no destination to send users to — the ad set fails at save time.
              </p>
            </div>

            <div className="rounded-lg border border-warning/30 bg-surface px-3 py-2.5">
              <div className="text-xs font-semibold text-text-primary mb-1.5">Two ways forward</div>
              <ol className="list-decimal pl-4 space-y-1 text-xs text-text-secondary leading-relaxed">
                <li>
                  <strong className="text-text-primary">Recommended — create a Lead Form in Meta Ads Manager:</strong>{' '}
                  Open the link below, build a form (name, privacy URL, questions like Email + Phone), publish it,
                  then click <strong>"I created one — recheck"</strong>.
                </li>
                <li>
                  <strong className="text-text-primary">Use a different lead destination:</strong>{' '}
                  Switch <strong>Conversion location</strong> above to <strong>Website</strong> (needs a Pixel),
                  <strong> Messenger</strong>, <strong>Calls</strong>, or <strong>App</strong>.
                </li>
              </ol>
            </div>

            <div className="flex flex-wrap items-center gap-2 pt-1">
              <a
                href="https://business.facebook.com/latest/instant_forms"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 rounded-lg border border-border bg-surface px-2.5 py-1 text-xs font-medium text-text-secondary hover:bg-surface-alt"
              >
                Create Lead Form in Meta <ExternalLink size={11} />
              </a>
              <button
                type="button"
                onClick={() => {
                  setFormsCheckLoading(true);
                  facebookLeadFormsService.list()
                    .then(setLeadForms)
                    .catch(() => setLeadForms([]))
                    .finally(() => setFormsCheckLoading(false));
                }}
                disabled={formsCheckLoading}
                className="inline-flex items-center gap-1 rounded-lg border border-border bg-surface px-2.5 py-1 text-xs font-medium text-text-secondary hover:bg-surface-alt disabled:opacity-50"
              >
                <RefreshCw size={11} className={formsCheckLoading ? 'animate-spin' : ''} /> I created one — recheck
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Standard goal hint — hide when the empty-forms banner is up to
          avoid duplicate warnings. */}
      {GOAL_HINTS[optGoal] && !showNoLeadFormsBanner && (
        <div
          className={
            'flex items-start gap-2 rounded-lg border px-3 py-2.5 text-xs ' +
            (GOAL_HINTS[optGoal].tone === 'warning'
              ? 'border-warning/40 bg-warning-soft'
              : 'border-info/40 bg-info-soft')
          }
        >
          {GOAL_HINTS[optGoal].tone === 'warning'
            ? <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warning" />
            : <Info size={14} className="mt-0.5 shrink-0 text-info" />}
          <p className="text-text-secondary leading-relaxed">{GOAL_HINTS[optGoal].body}</p>
        </div>
      )}

      {location === 'APP' && (
        <FormField label="Facebook App" required hintBelow hint="Pick from apps you've registered at developers.facebook.com.">
          <FbAppPicker value={fbAppId} mobileOnly onChange={(id, app) => { setFbAppId(id); setPickedApp(app); }} />
        </FormField>
      )}

      {/* Pixel + event picker — only when the goal is conversion-based on a
          website. Without these, Meta has no way to know what counts as a
          successful "lead" on the site. */}
      {needsPixel && (
        <Card padded className="!mt-5">
          <SectionHeader
            title="Pixel & conversion event"
            icon={<Activity size={16} />}
            subtitle="Tracks when someone converts on your website. Without a Pixel + an event firing, Conversions optimisation can't run."
          />
          <FormField label="Meta Pixel" required hintBelow hint="Pick a pixel installed on your destination site.">
            <FbPixelPicker value={pixelId} onChange={(id) => setPixelId(id)} />
          </FormField>
          <FormField label="Conversion event" required hintBelow hint="Which event on your site counts as a 'lead'.">
            <Select value={eventType} onChange={(e) => setEventType(e.target.value)}>
              {LEAD_CONV_EVENTS.map((ev) => (
                <option key={ev} value={ev}>{ev.replace(/_/g, ' ')}</option>
              ))}
            </Select>
          </FormField>
          <p className="mt-1.5 text-2xs text-text-muted leading-relaxed">
            <Info size={11} className="inline mr-0.5" />
            The selected event must already be set up on your Pixel and have fired at least once
            from real site traffic. If you haven\'t set this up yet, the easiest path is to switch
            <strong className="text-text-primary"> Conversion location</strong> to <strong>Instant Forms</strong> above —
            those use Meta-hosted forms and don\'t need a Pixel.
          </p>
        </Card>
      )}

      {needsPagePicker && (
        <Card padded className="!mt-5">
          <SectionHeader
            title="Where ads will run"
            icon={<MapPin size={16} />}
            subtitle="Lead ads must be tied to a Facebook Page; Instagram is optional"
          />
          <FormField label="Facebook Page" required>
            <FbPagePicker value={pageId} restrictToAuthorized onChange={(id) => setPageId(id)} />
          </FormField>
          <FormField label="Instagram (optional)">
            <IgAccountPicker
              value={igActorId}
              onChange={(id) => setIgActorId(id)}
              disabled={!igAllowed}
              disabledReason="Instagram is excluded from your Placements selection. Add Instagram under Manual placements to enable this."
            />
          </FormField>
        </Card>
      )}

      <AttributionWindowSection
        clickWin={clickWin}
        setClickWin={setClickWin}
        viewWin={viewWin}
        setViewWin={setViewWin}
        hideViewThrough={!allowsLongerAttribution}
        hint={
          allowsLongerAttribution
            ? 'Website + Conversions supports the standard 7-day click + 1-day view window.'
            : 'Meta only accepts a 1-day click attribution window for this goal — auto-set above. View-through doesn\'t apply here.'
        }
      />
    </BaseAdSetForm>
  );
};
